import type { DatabaseFile } from '../types';
import { applyUnsavedItemEdits } from '../utils/itemEditorDraft';
import { withoutGitBusyOverlay } from '../utils/gitBusy';
import { appAlert, appConfirm } from '../utils/appDialog';
import { formatSaveFailureHint } from '../utils/saveDebugLog';
import { renderPageIconHtml } from '../utils/pageIcon';
import type { FileViewDeps } from './fileView/FileViewDeps';
import { ImageLightbox } from './fileView/ImageLightbox';
import { FileViewChrome } from './fileView/FileViewChrome';
import { FileViewSections, type FileViewSectionsState } from './fileView/FileViewSections';

export type { PagePickerOption } from './fileView/FileViewDeps';

export class FileView {
  private readonly deps: FileViewDeps;
  private readonly imageLightbox: ImageLightbox;
  private readonly sectionsState: FileViewSectionsState = {
    pendingBlobUrls: [],
    itemGestureAbort: null,
    sectionDragGestureAbort: null,
    sectionsPointerAbort: null,
    mediaHydrateGeneration: 0,
    activeContainer: null,
    activeFile: null,
    activeOnDirty: null,
  };
  private readonly sections: FileViewSections;
  private readonly chrome: FileViewChrome;
  private dirty = false;
  private container: HTMLElement | null = null;
  private currentFile: DatabaseFile | null = null;
  /** Stable callback wired into section handlers; must not be a no-op after save refresh. */
  private markDirtyFn: (() => void) | null = null;

  constructor(deps: FileViewDeps) {
    this.deps = deps;
    this.imageLightbox = new ImageLightbox(deps.resolveImageUrl);
    this.sections = new FileViewSections(deps, this.sectionsState);
    this.chrome = new FileViewChrome(deps, this.sections);
  }

  private get onSave() {
    return this.deps.onSave;
  }
  private get onDiscard() {
    return this.deps.onDiscard;
  }

  hasUnsavedChanges(): boolean {
    return this.dirty;
  }

  getCurrentFilePath(): string | null {
    return this.currentFile?.path ?? null;
  }

  /** Re-render checklist UI after save so committed media hydrates from GitHub. */
  refreshMediaIfCurrent(file: DatabaseFile): void {
    if (this.currentFile?.path !== file.path || !this.container || !this.markDirtyFn) return;
    this.sections.renderSections(this.container, file, this.markDirtyFn);
  }

  /** Mark unsaved and re-render the open page (staged item moves). */
  markDirtyAndRefresh(): void {
    if (!this.container || !this.currentFile || !this.markDirtyFn) return;
    this.markDirtyFn();
    this.sections.renderSections(this.container, this.currentFile, this.markDirtyFn);
  }

  /** Scroll main content to a section or item after sidebar search navigation. */
  scrollToSearchTarget(sectionIndex?: number, itemIndex?: number): void {
    if (!this.container || sectionIndex === undefined) return;
    const sectionsEl = this.container.querySelector<HTMLElement>('#sections-container');
    if (!sectionsEl) return;

    const block = sectionsEl.querySelector<HTMLElement>(`.section-block[data-section="${sectionIndex}"]`);
    const details = block?.querySelector<HTMLDetailsElement>('details.section');
    if (details) details.open = true;

    const row =
      itemIndex !== undefined
        ? sectionsEl.querySelector<HTMLElement>(
            `.item-row[data-section="${sectionIndex}"][data-item="${itemIndex}"]`
          )
        : null;
    const target = row ?? block;
    target?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    if (row) {
      row.classList.add('item-row-search-flash');
      window.setTimeout(() => row.classList.remove('item-row-search-flash'), 2600);
    }
  }

  render(container: HTMLElement, file: DatabaseFile): void {
    this.deps.onBeforeDocumentRender?.();
    this.dirty = false;
    this.container = container;
    this.currentFile = file;

    const canRenameOrDelete = /^database\/.+\.md$/.test(file.path);
    const pageIconHtml = renderPageIconHtml(this.deps.getPageIcon?.(file.path) ?? '📄', 'page-icon page-icon--header');
    const iconPickerBtn =
      canRenameOrDelete && this.deps.onSetPageIcon
        ? `<button type="button" id="page-icon-btn" class="btn-page-icon" title="Change sidebar icon — search emoji and symbols" aria-label="Change sidebar icon">${pageIconHtml}</button>`
        : '';

    container.innerHTML = `
      <div class="file-view">
        <div class="file-header">
          <div class="file-title-row">
            ${iconPickerBtn}
            <h2 class="file-title" id="file-title-display">${file.name}</h2>
            <button type="button" id="btn-share-page" class="btn-icon" title="Generate encrypted link for this page">🔗</button>
            ${canRenameOrDelete ? `<button id="rename-btn" class="btn-icon" title="Rename file">✏️</button>` : ''}
            ${canRenameOrDelete && this.deps.onMoveFileToFolder ? `<button id="move-file-btn" class="btn-icon" title="Move page to another folder">📂</button>` : ''}
            ${canRenameOrDelete ? `<button id="delete-file-btn" class="btn-icon btn-icon-danger" title="Delete this file">🗑</button>` : ''}
          </div>
        </div>
        <div id="sections-container" class="sections-container"></div>
        <div class="add-section-bar">
          <button id="add-section-btn" class="btn-add-section">＋ Add section</button>
        </div>
      </div>
      <div id="file-save-dock" class="file-save-dock hidden" role="region" aria-label="Unsaved changes">
        <div class="file-save-dock-status">
          <span class="dirty-indicator">● Unsaved changes</span>
        </div>
        <div class="file-save-dock-actions">
          <button type="button" id="discard-btn-dock" class="btn-dock btn-dock-discard" disabled><span class="btn-dock-label">Discard</span></button>
          <button type="button" id="save-btn-dock" class="btn-dock btn-dock-save" disabled><span class="btn-dock-label">Save</span></button>
        </div>
      </div>
    `;

    const fileViewEl = container.querySelector<HTMLElement>('.file-view')!;
    const saveBtnDock = container.querySelector<HTMLButtonElement>('#save-btn-dock')!;
    const discardBtnDock = container.querySelector<HTMLButtonElement>('#discard-btn-dock')!;
    const saveDock = container.querySelector<HTMLElement>('#file-save-dock')!;

    const setSaveLabels = (text: string) => {
      saveBtnDock.querySelector<HTMLElement>('.btn-dock-label')!.textContent = text;
    };

    const markDirty = () => {
      this.dirty = true;
      saveBtnDock.disabled = false;
      discardBtnDock.disabled = false;
      fileViewEl.classList.add('file-view--dirty');
      saveDock.classList.remove('hidden');
    };
    this.markDirtyFn = markDirty;

    if (applyUnsavedItemEdits(file)) markDirty();

    this.sections.renderSections(container, file, markDirty);
    this.imageLightbox.wire(container);

    const onSaveClick = async () => {
      saveBtnDock.disabled = true;
      discardBtnDock.disabled = true;
      setSaveLabels('Saving…');
      try {
        await withoutGitBusyOverlay(() => this.onSave(file));
        this.dirty = false;
        fileViewEl.classList.remove('file-view--dirty');
        saveBtnDock.disabled = true;
        discardBtnDock.disabled = true;
        setSaveLabels('Save');
        saveDock.classList.add('hidden');
      } catch (err) {
        saveBtnDock.disabled = false;
        discardBtnDock.disabled = false;
        setSaveLabels('Save');
        await appAlert(formatSaveFailureHint(err), 'Save failed');
      }
    };
    saveBtnDock.addEventListener('click', onSaveClick);

    discardBtnDock.addEventListener('click', () => {
      void (async () => {
        const ok = await appConfirm('Discard all unsaved changes?', {
          title: 'Discard changes',
          confirmLabel: 'Discard',
          danger: true,
        });
        if (!ok) return;
        this.onDiscard(file);
      })();
    });

    this.chrome.wireAddSection(container, file, markDirty);
    this.chrome.wireSharePage(container, file);
    if (canRenameOrDelete) {
      if (this.deps.onSetPageIcon) this.chrome.wirePageIcon(container, file);
      this.chrome.wireRename(container, file);
      if (this.deps.onMoveFileToFolder && this.deps.listFolders) {
        this.chrome.wireMoveFile(container, file);
      }
      this.chrome.wireDeleteFile(container, file);
    }
  }
}
